/**
 * The browser-only half of the deletion ledger: the `onChange` recorder, which
 * schedules through `requestAnimationFrame` and writes via the imperative API.
 * The pure helpers live in `packages/mindmap-core` so `functions/` shares them.
 */
import {
  findMindmapRootElement,
  recordMindmapDeletions,
  userDeletedMindmapBlocks,
} from "../../packages/mindmap-core/src/mindmapElements";

import type { ExcalidrawElement } from "../../packages/excalidraw/element/types";
import type { MindmapDeletionEntry } from "../../packages/mindmap-core/src/mindmapElements";

/** Frames a batch may wait for a writable scene before it is given up on. */
const LEDGER_FLUSH_ATTEMPTS = 5;

/** The scene reader/writer a deletion recorder needs (Excalidraw's imperative API). */
interface MindmapLedgerApi {
  getSceneElementsIncludingDeleted: () => readonly ExcalidrawElement[];
  updateScene: (data: {
    elements: readonly ExcalidrawElement[];
    commitToHistory: boolean;
  }) => void;
}

/**
 * A scene-change handler that keeps the ledger up to date, for wiring into the
 * app's `onChange`.
 *
 * `onChange` fires from Excalidraw's commit phase, which is why the write is
 * deferred through `schedule` (a frame) rather than issued inline: an
 * updateScene from inside the commit is the exact shape of the React #185
 * nested-update crash. It also short-circuits before scheduling anything when no
 * map node was deleted, so the common path costs one array scan and nothing
 * else — no updateScene, no autosave churn. Deletions arriving while a write is
 * pending are batched into it, and a batch that finds no writable scene is kept
 * for a later flush rather than dropped. The write rides `commitToHistory: false`: the
 * user's undo must revert their deletion, not our bookkeeping.
 */
export const createMindmapDeletionRecorder = (
  getApi: () => MindmapLedgerApi | null,
  schedule: (fn: () => void) => void = (fn) => requestAnimationFrame(fn),
): ((elements: readonly ExcalidrawElement[]) => void) => {
  let prev: readonly ExcalidrawElement[] = [];
  const pending = new Map<string, MindmapDeletionEntry[]>();
  let scheduled = false;
  let attempts = 0;

  const flush = (): void => {
    scheduled = false;
    const api = getApi();
    if (api) {
      const scene = api.getSceneElementsIncludingDeleted();
      const next = recordMindmapDeletions(scene, pending);
      if (next) {
        api.updateScene({ elements: next, commitToHistory: false });
      }
      // A map whose root is on canvas has been written (or was already
      // recorded); anything else is still unwritten and must survive the flush.
      for (const mapId of [...pending.keys()]) {
        if (findMindmapRootElement(scene, mapId)) {
          pending.delete(mapId);
        }
      }
    }
    if (pending.size === 0) {
      attempts = 0;
      return;
    }
    // The API is wired up a frame or two after mount, so the first deletion can
    // land before there is anywhere to write it. Retry a bounded number of
    // frames rather than dropping the batch outright — but bounded, because a
    // map whose root never appears would otherwise reschedule forever.
    if (attempts >= LEDGER_FLUSH_ATTEMPTS) {
      pending.clear();
      attempts = 0;
      return;
    }
    attempts += 1;
    scheduled = true;
    schedule(flush);
  };

  return (elements: readonly ExcalidrawElement[]): void => {
    const deleted = userDeletedMindmapBlocks(prev, elements);
    prev = elements;
    if (deleted.size === 0) {
      return;
    }
    for (const [mapId, entries] of deleted) {
      pending.set(mapId, [...(pending.get(mapId) ?? []), ...entries]);
    }
    if (scheduled) {
      return;
    }
    attempts = 0;
    scheduled = true;
    schedule(flush);
  };
};
